import { EventEmitter } from 'events';
import { OdspErrorType } from '@fluidframework/odsp-driver-definitions';
import { DriverErrorType } from '@fluidframework/driver-definitions';
import { ContainerErrorType } from '@fluidframework/container-definitions';
import { BannerType, OWHErrorType } from '@fluidx/office-fluid-types';
import { NamespaceLoggerAdapter, sendTelemetryEvent } from '@fluidx/telemetry';
import { UserActivityTracker, isMobile, isOdspResolvedUrl, getDebouncedFunction } from '@fluidx/utilities';
import { getFFXHostUxIcon } from '@fluidx/icons/lib/useIconNames/ffxHostUx';
import { getStringTableIfNeeded, isStringTable } from '../../../utilities/localizationUtilities.dynamic';
import { ActionDebouncer } from '../../../utilities/ActionDebouncer';
import { GenericEvent, GenericEventSubcategories } from '../../../telemetry/GenericEvent';
import { ContainerConnectionStateTracker } from './ContainerConnectionStateTracker';
import { getBannerMessagingForError, getBannerMessagingForContainerState } from './getBannerMessagingForError';
const disconnectedBannerType = 'Disconnected';
const connectionStateDisconnected = 0;
/**
 * Returns a banner action if there is an available callback that could handle it. If there is not an available callback, returns undefined
 * @param containerBannerActionHandlers the handlers for the container banner provider.
 *  Either a single callback that can be called for all action types, or a (partial) mapping between action types and handlers
 * @param actionLabel The text label for the action button if a BannerAction is returned
 * @param actionType The type of action for error recovery
 * @param errorType The type of error to be passed to the handler
 * @param onHandlerExecuted Callback to be called after the action handler is called
 * @returns The BannerAction, if a handler is present. Otherwise undefined
 */ const getBannerAction = (containerBannerActionHandlers, actionLabel, actionType, errorType, onHandlerExecuted)=>{
    let actionCallback = undefined;
    if (typeof containerBannerActionHandlers === 'function') {
        if (errorType) {
            actionCallback = ()=>{
                containerBannerActionHandlers(actionType, errorType);
                onHandlerExecuted?.();
            };
        }
    } else {
        const mappedHandler = containerBannerActionHandlers[actionType];
        if (mappedHandler) {
            actionCallback = ()=>{
                mappedHandler(errorType);
                onHandlerExecuted?.();
            };
        }
    }
    return actionCallback ? {
        label: actionLabel,
        actionCallback
    } : undefined;
};
/**
 * A message banner provider that defines and produces banners in response to
 * PostLoad office fluid container state signals (mostly errors and warnings)
 *
 * Where this banner provider is too opinionated on things like recovery actions possible for each error handled,
 * It's recommended a host builds their own equivalent banner provider composing or extending
 * out-of-box functionality provided here.
 *
 * Alternatively, hosts can build their fully custom provider using with the underlying error handling utilities leveraged
 */ export class ContainerBannerProvider extends EventEmitter {
    get MessageBannerProvider() {
        return this;
    }
    reset() {
        this.hasActiveError = false;
        this.removeAllListeners();
        this.fileVersion = undefined;
        window.clearTimeout(this.throttleBannerDismissTimeoutId);
        this.throttleBannerDismissTimeoutId = undefined;
    }
    dispose() {
        this.reset();
        this.container.off('warning', this.containerWarningHandler);
        this.container.off('closed', this.containerClosedHandler);
        this.userActivityTracker?.dispose();
        this.connectionStateTracker?.dispose();
        this.readonlyStateTracker?.dispose();
    }
    emitVersionBannerSignal(stringTable) {
        const bannerId = 'VersionHistoryBanner';
        this.emit('CreateBanner', {
            bannerId,
            bannerType: BannerType.warning,
            bannerStrings: {
                bannerTitle: stringTable.versionBannerTitle,
                bannerMessage: stringTable.versionBannerMessage
            },
            isDismissible: false
        });
    }
    constructor(container, stringTableOrLocale, containerBannerActionHandlers, logger){
        super();
        this.container = container;
        this.stringTableOrLocale = stringTableOrLocale;
        this.containerBannerActionHandlers = containerBannerActionHandlers;
        this.hasActiveError = false;
        this.debouncedFileVersionCheck = getDebouncedFunction(()=>{
            if (this.container.resolvedUrl && isOdspResolvedUrl(this.container.resolvedUrl)) {
                this.fileVersion = this.container.resolvedUrl.fileVersion;
            }
            if (this.fileVersion) {
                getStringTableIfNeeded(this.stringTableOrLocale).then((stringTable)=>{
                    this.stringTable = stringTable;
                    this.emitVersionBannerSignal(this.stringTable);
                }).catch(()=>{
                    this.logger && sendTelemetryEvent(this.logger, {
                        subCategory: GenericEventSubcategories.MessageBanner,
                        eventName: GenericEvent.StringTableFetchError
                    });
                });
            }
        }, 0);
        this.containerWarningHandler = async (warning)=>{
            this.stringTable = await getStringTableIfNeeded(this.stringTableOrLocale);
            const bannerWarningInfo = await getBannerMessagingForError(warning, this.stringTable);
            // We only show banners for container throttling
            if (bannerWarningInfo.errorType === ContainerErrorType.throttlingError) {
                const throttleBannerDefinition = await this.selectBannerDefinition(bannerWarningInfo);
                this.emit('CreateBanner', throttleBannerDefinition);
                if ('retryAfterSeconds' in warning && typeof warning.retryAfterSeconds === 'number') {
                    const onThrottleRetryDurationElapsed = ()=>{
                        this.emit('RemoveBanner', throttleBannerDefinition.bannerId);
                    };
                    this.throttleBannerDismissTimeoutId = setTimeout(onThrottleRetryDurationElapsed, warning.retryAfterSeconds * 1000);
                }
            }
        };
        this.containerClosedHandler = async (error)=>{
            /**
     * Container may close for a variety of reasons, including the fact that a host may choose to close it
     * Only react to container close event with an error dialog if there's an error associated with the event
     */ if (error) {
                this.stringTable = await getStringTableIfNeeded(this.stringTableOrLocale);
                const bannerErrorInfo = await getBannerMessagingForError(error, this.stringTable);
                this.emit('RemoveBanner', disconnectedBannerType);
                this.emit('CreateBanner', await this.selectBannerDefinition(bannerErrorInfo));
                this.hasActiveError = true;
            }
            // Once the container is closed, we no longer need to listen for further events.
            this.dispose();
        };
        this.startConnectionTracker = ()=>{
            this.connectionStateTracker = new ContainerConnectionStateTracker(this.container, async (newConnectionState, disconnectedReason, containerDirty, containerClosed)=>{
                if (newConnectionState === 'connected') {
                    this.emit('RemoveBanner', disconnectedBannerType);
                } else {
                    // Container is disconnected as a result of the container being closed
                    // Other forms of error UX are shown on container close, so showing disconnected banner is redundant
                    if (containerClosed) {
                        // TODO: Task 6894830: Cleanup DisconnectedBannerNotShown event after quantifying impact
                        this.logger && sendTelemetryEvent(this.logger, {
                            subCategory: GenericEventSubcategories.MessageBanner,
                            eventName: GenericEvent.DisconnectedBannerNotShown,
                            reason: 'ContainerClosed'
                        });
                        return;
                    }
                    // User is inactive, so don't show the disconnected banner but track that we didn't show a banner
                    if (this.userActivityTracker?.isUserActive === false) {
                        // TODO: Task 6894830: Cleanup DisconnectedBannerNotShown event after quantifying impact
                        this.logger && sendTelemetryEvent(this.logger, {
                            subCategory: GenericEventSubcategories.MessageBanner,
                            eventName: GenericEvent.DisconnectedBannerNotShown,
                            reason: 'UserInactive'
                        });
                        return;
                    }
                    // The container is disconnected due to other reasons, so show the message banner
                    this.stringTable = await getStringTableIfNeeded(this.stringTableOrLocale);
                    const bannerStrings = await getBannerMessagingForContainerState(disconnectedBannerType, this.stringTable);
                    this.emit('CreateBanner', {
                        bannerId: disconnectedBannerType,
                        bannerType: BannerType.warning,
                        bannerStrings,
                        icon: getFFXHostUxIcon('FFXInfo'),
                        isDismissible: false,
                        additionalFields: {
                            disconnectedReason,
                            containerDirty
                        }
                    });
                    this.logger && sendTelemetryEvent(this.logger, {
                        subCategory: GenericEventSubcategories.MessageBanner,
                        eventName: GenericEvent.DisconnectedBannerShown
                    });
                }
            });
            // How long have we seen no activity from the user before we treat them as idle/inactive. Set to 1 hour.
            const userInactiveAfterMs = 60 * 60 * 1000;
            this.userActivityTracker = new UserActivityTracker(userInactiveAfterMs, this.onUserActivityStateChange, this.logger);
        };
        this.onUserActivityStateChange = (isActive)=>{
            // If the user becomes idle, we disconnect so that we do not auto-reconnect the container on disconnection. This is done to save COGS on the server while the user is idle.
            // If the user becomes active, we connect again and try to reconnect container if it is disconnected.
            // TODO: Move reconnect logic lower in the stack based on generic browser idle properties
            // TODO: Task 6177501: Re-evaluate ContainerBannerProvider use of "Disconnected" state, in light of FF 1.0
            // (which also exposes new "EstablishingConnection" state)
            // In FF 1.0 the ConnectionState types have been updated to include a new state which previously was encompassed by the Disconnected state.
            // The new state is EstablishingConnection and indicates that the container is attempting to connect to the ordering service, but is not yet connected.
            // Per their BREAKING.md, "Any logic based on the Disconnected state (e.g. checking the value of connectionState on either IContainer and Container)
            // should be updated depending on how you want to treat this new EstablishingConnection state."
            if (!this.container.closed) {
                if (isActive && this.container.connectionState === connectionStateDisconnected) {
                    this.container.connect();
                    // Since we were not trying to establish a connection while the user was inactive, let's reset the connection state tracker to
                    // give the app a chance to try an establish a connection.
                    // TODO: Task 4902117: Reset provider state on banner dismiss
                    this.emit('RemoveBanner', disconnectedBannerType);
                    this.connectionStateTracker?.reset();
                } else if (!isActive && this.container.connectionState !== connectionStateDisconnected) {
                    // If inactive and connected, disconnect connection to save on COGs while user is inactive
                    this.container.disconnect();
                    // Set the disconnect reason for the disconnect
                    this.connectionStateTracker?.setLastDisconnectedReason('UserInactive');
                }
            }
        };
        this.startReadonlyStateTracker = ()=>{
            const readonlyState = isMobile() ? 'ReadonlyMobile' : 'Readonly';
            this.readonlyStateTracker = new ReadonlyStateTracker(this.container, async ()=>{
                if (!this.hasActiveError && this.fileVersion === undefined) {
                    this.stringTable = await getStringTableIfNeeded(this.stringTableOrLocale);
                    const bannerStrings = await getBannerMessagingForContainerState(readonlyState, this.stringTable);
                    this.emit('CreateBanner', {
                        bannerId: readonlyState,
                        bannerType: BannerType.warning,
                        bannerStrings,
                        icon: getFFXHostUxIcon('FFXInfo')
                    });
                }
            }, ()=>{
                this.emit('RemoveBanner', readonlyState);
            });
        };
        this.selectBannerDefinition = async ({ errorType , bannerStrings  })=>{
            if (!this.stringTable) {
                this.stringTable = await getStringTableIfNeeded(this.stringTableOrLocale);
            }
            switch(errorType){
                case OWHErrorType.ContainerUpgradeRequired:
                    {
                        const upgradeAction = getBannerAction(this.containerBannerActionHandlers /* containerBannerActionHandlers */ , this.stringTable.upgradeButtonText /* actionLabel */ , 'Upgrade' /* actionType */ , errorType /* errorType */ );
                        return {
                            bannerId: errorType,
                            bannerType: BannerType.warning,
                            bannerStrings,
                            icon: getFFXHostUxIcon('FFXError'),
                            actions: upgradeAction ? [
                                upgradeAction
                            ] : undefined
                        };
                    }
                case ContainerErrorType.throttlingError:
                    {
                        // ContainerErrorType.throttlingError is recoverable so thus should be displayed as a warning message banner
                        // See: https://github.com/microsoft/FluidFramework/issues/9821 and https://fluidframework.visualstudio.com/internal/_workitems/edit/811
                        return {
                            bannerId: errorType,
                            bannerType: BannerType.warning,
                            bannerStrings,
                            icon: getFFXHostUxIcon('FFXError')
                        };
                    }
                case DriverErrorType.fileNotFoundOrAccessDeniedError:
                case DriverErrorType.authorizationError:
                    {
                        return {
                            bannerId: errorType,
                            bannerType: BannerType.error,
                            bannerStrings,
                            icon: getFFXHostUxIcon('FFXErrorBadge')
                        };
                    }
                case DriverErrorType.fileOverwrittenInStorage:
                    {
                        const reloadAction = getBannerAction(this.containerBannerActionHandlers /* containerBannerActionHandlers */ , this.stringTable.reloadButtonText /* actionLabel */ , 'Reload' /* actionType */ , errorType /* errorType */ );
                        return {
                            bannerId: errorType,
                            bannerType: BannerType.warning,
                            bannerStrings,
                            icon: getFFXHostUxIcon('FFXErrorBadge'),
                            actions: reloadAction ? [
                                reloadAction
                            ] : undefined
                        };
                    }
                case OdspErrorType.outOfStorageError:
                    {
                        const viewFileLocationAction = getBannerAction(this.containerBannerActionHandlers /* containerBannerActionHandlers */ , this.stringTable.viewFileLocationButtonText /* actionLabel */ , 'ViewFileLocation' /* actionType */ , errorType /* errorType */ );
                        return {
                            bannerId: errorType,
                            bannerType: BannerType.error,
                            bannerStrings,
                            icon: getFFXHostUxIcon('FFXErrorBadge'),
                            actions: viewFileLocationAction ? [
                                viewFileLocationAction
                            ] : undefined
                        };
                    }
                case DriverErrorType.throttlingError:
                case DriverErrorType.genericError:
                case ContainerErrorType.genericError:
                default:
                    {
                        const reloadAction = getBannerAction(this.containerBannerActionHandlers /* containerBannerActionHandlers */ , this.stringTable.reloadButtonText /* actionLabel */ , 'Reload' /* actionType */ , errorType /* errorType */ , ()=>{
                            this.hasActiveError = false;
                        } /* onHandlerExecuted */ );
                        return {
                            bannerId: errorType,
                            bannerType: BannerType.error,
                            bannerStrings,
                            icon: getFFXHostUxIcon('FFXErrorBadge'),
                            actions: reloadAction ? [
                                reloadAction
                            ] : undefined
                        };
                    }
            }
        };
        /** Apply error namespace to telemetry logger */ this.logger = logger && new NamespaceLoggerAdapter('ErrorUX', logger);
        this.startConnectionTracker();
        this.startReadonlyStateTracker();
        this.container.on('warning', this.containerWarningHandler);
        this.container.on('closed', this.containerClosedHandler);
        if (isStringTable(stringTableOrLocale)) {
            this.stringTable = stringTableOrLocale;
        }
        /**
     * We are debouncing the file version check to allow the FluidMessageBanner action handlers
     * to be added to the container banner provider. Without this, there is a race condition between
     * when the handler is mounted and when we emit the createBanner event. If the emit happens first,
     * there is no handler to catch the event and results in a no-op.
     */ this.debouncedFileVersionCheck();
    }
}
/**
 * This state tracker uses an action debouncer to aggregate readonly and error states when creating readonly banners.
 * On a banner signal, create readonly banner action is deferred (debounced) for a known period of time to allow for
 * any 'just-received', or 'just-to-be-received' error signals to take priority in messaging
 */ class ReadonlyStateTracker {
    dispose() {
        this.container.off('readonly', this.containerReadonlyHandler);
        this.createReadonlyDebouncer.dispose();
    }
    constructor(container, createReadonlyBanner, removeReadonlyBanner){
        this.container = container;
        this.createReadonlyBanner = createReadonlyBanner;
        this.removeReadonlyBanner = removeReadonlyBanner;
        this.debounceReadonlyNotificationMs = 100;
        this.containerReadonlyHandler = (readonly)=>{
            if (readonly) {
                this.createReadonlyDebouncer.startOrRefreshDebounce();
            } else {
                this.createReadonlyDebouncer.cancelPendingDebounce();
                this.removeReadonlyBanner();
            }
        };
        this.createReadonlyDebouncer = new ActionDebouncer(this.createReadonlyBanner, this.debounceReadonlyNotificationMs);
        this.container.on('readonly', this.containerReadonlyHandler);
    }
} // cspell:ignore Debouncer
